/**
 * ViewTemplatePage — View Template Admin
 *
 * CRUD management for view templates (list view column configs, detail layouts, etc.).
 * Each template targets an entitySlug + viewType and can be set as the default.
 *
 * API: GET/POST/PATCH/DELETE /api/v1/view-templates, POST /:id/set-default
 */

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Search,
  RefreshCw,
  Plus,
  Trash2,
  Star,
  Pencil,
  X,
  Save,
  FileCode,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ─────────────────────────────────────────

interface ViewTemplate {
  id: string;
  entity_slug: string;
  view_type: string;
  name: string;
  description?: string;
  template: string; // JSON string
  is_default: boolean;
  role_id?: string;
  version: number;
  created_at: string;
  updated_at: string;
}

type FormData = {
  entitySlug: string;
  viewType: string;
  name: string;
  description: string;
  template: string;
};

const EMPTY_FORM: FormData = { entitySlug: '', viewType: 'list', name: '', description: '', template: '{}' };

// ─── Component ─────────────────────────────────────

export default function ViewTemplatePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [templates, setTemplates] = useState<ViewTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<string | null>(null); // template id being edited
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // ── Fetch ──

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ items: ViewTemplate[]; total: number }>('/view-templates?limit=100');
      setTemplates(res.items ?? []);
    } catch (err) {
      console.error('Failed to fetch view templates', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchTemplates(); }, [fetchTemplates]);

  useEffect(() => {
    const entitySlug = searchParams.get('entitySlug') ?? '';
    const viewType = searchParams.get('viewType') ?? 'list';
    const templateName = searchParams.get('templateName') ?? '';
    const shouldCreate = searchParams.get('create') === '1';

    if (entitySlug) {
      setSearch(entitySlug);
      setForm((prev) => ({
        ...prev,
        entitySlug,
        viewType,
        name: templateName || prev.name,
      }));
    }

    if (shouldCreate) {
      setCreating(true);
      setEditing(null);
    }
  }, [searchParams]);

  // ── Filter ──

  const filtered = templates.filter((tpl) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      tpl.name.toLowerCase().includes(q) ||
      tpl.entity_slug.toLowerCase().includes(q) ||
      tpl.view_type.toLowerCase().includes(q)
    );
  });

  // ── Actions ──

  const handleCreate = async () => {
    if (!form.entitySlug || !form.name) return;
    setSaving(true);
    try {
      await api.post('/view-templates', {
        entitySlug: form.entitySlug,
        viewType: form.viewType,
        name: form.name,
        description: form.description || undefined,
        template: form.template,
      });
      setCreating(false);
      setForm(EMPTY_FORM);
      await fetchTemplates();
    } catch (err) {
      console.error('Failed to create template', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id: string) => {
    setSaving(true);
    try {
      await api.patch(`/view-templates/${id}`, {
        name: form.name,
        description: form.description || undefined,
        template: form.template,
      });
      setEditing(null);
      setForm(EMPTY_FORM);
      await fetchTemplates();
    } catch (err) {
      console.error('Failed to update template', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('admin.viewTemplates.confirmDelete', 'Delete this template?'))) return;
    try {
      await api.delete(`/view-templates/${id}`);
      await fetchTemplates();
    } catch (err) {
      console.error('Failed to delete template', err);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await api.post(`/view-templates/${id}/set-default`, {});
      await fetchTemplates();
    } catch (err) {
      console.error('Failed to set default', err);
    }
  };

  const startEdit = (tpl: ViewTemplate) => {
    setEditing(tpl.id);
    setCreating(false);
    setForm({
      entitySlug: tpl.entity_slug,
      viewType: tpl.view_type,
      name: tpl.name,
      description: tpl.description ?? '',
      template: typeof tpl.template === 'string' ? tpl.template : JSON.stringify(tpl.template, null, 2),
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setCreating(false);
    setForm(EMPTY_FORM);
  };

  // ── Render ──

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileCode className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold">{t('admin.viewTemplates.title', 'View Templates')}</h1>
          <Badge variant="secondary">{templates.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void fetchTemplates()} disabled={loading}>
            <RefreshCw className={cn('h-4 w-4 mr-1', loading && 'animate-spin')} />
            {t('common.refresh', 'Refresh')}
          </Button>
          <Button
            size="sm"
            onClick={() => { setCreating(true); setEditing(null); setForm(EMPTY_FORM); }}
            disabled={creating}
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('common.create', 'Create')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('admin.viewTemplates.search', 'Search by name, entity, or type...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
        />
      </div>

      {/* Create Form */}
      {creating && (
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm">{t('admin.viewTemplates.createNew', 'New View Template')}</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Input
                placeholder="Entity Slug (e.g. jobs)"
                value={form.entitySlug}
                onChange={(e) => setForm((f) => ({ ...f, entitySlug: e.target.value }))}
              />
              <Input
                placeholder="View Type (e.g. list, detail)"
                value={form.viewType}
                onChange={(e) => setForm((f) => ({ ...f, viewType: e.target.value }))}
              />
              <Input
                placeholder="Name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <Input
              placeholder="Description (optional)"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
            <textarea
              className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono min-h-[120px] resize-y"
              placeholder='{"columns": [...]}'
              value={form.template}
              onChange={(e) => setForm((f) => ({ ...f, template: e.target.value }))}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={cancelEdit}>
                <X className="h-4 w-4 mr-1" /> {t('common.cancel', 'Cancel')}
              </Button>
              <Button size="sm" onClick={handleCreate} disabled={saving || !form.entitySlug || !form.name}>
                <Save className="h-4 w-4 mr-1" /> {t('common.save', 'Save')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template List */}
      {loading && templates.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {t('admin.viewTemplates.empty', 'No view templates found.')}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((tpl) => (
            <Card key={tpl.id} className={cn(editing === tpl.id && 'ring-2 ring-primary')}>
              <CardContent className="px-4 py-3">
                {editing === tpl.id ? (
                  /* Edit mode */
                  <div className="space-y-3">
                    <Input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Name"
                    />
                    <Input
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="Description"
                    />
                    <textarea
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono min-h-[120px] resize-y"
                      value={form.template}
                      onChange={(e) => setForm((f) => ({ ...f, template: e.target.value }))}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button variant="ghost" size="sm" onClick={cancelEdit}>
                        <X className="h-4 w-4 mr-1" /> {t('common.cancel', 'Cancel')}
                      </Button>
                      <Button size="sm" onClick={() => handleUpdate(tpl.id)} disabled={saving}>
                        <Save className="h-4 w-4 mr-1" /> {t('common.save', 'Save')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* View mode */
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{tpl.name}</span>
                          {tpl.is_default && (
                            <Badge variant="default" className="text-xs">
                              <Star className="h-3 w-3 mr-0.5" /> Default
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-xs">{tpl.entity_slug}</Badge>
                          <Badge variant="outline" className="text-xs">{tpl.view_type}</Badge>
                          <span>v{tpl.version}</span>
                          {tpl.description && <span className="truncate max-w-[200px]">— {tpl.description}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!tpl.is_default && (
                        <Button variant="ghost" size="sm" onClick={() => handleSetDefault(tpl.id)} title="Set as default">
                          <Star className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => startEdit(tpl)} title="Edit">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(tpl.id)} title="Delete" className="text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
